import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ActionExecutionContext } from '../../../runtime/actions/action-executor';
import type { ResolvedAction } from '../../../runtime/actions/action-resolution';
import { ActionRenderer } from '../ActionRenderer';

export interface TableEmptyStateProps {
  title?: string;
  message?: string;
  actions?: ResolvedAction[];
  onAction?: (action: ResolvedAction, context?: ActionExecutionContext) => void | Promise<void>;
  actionContext?: ActionExecutionContext;
  /** When true, records are not directly mutable; authorized page actions may still exist. */
  readOnly?: boolean;
}

/** Compact table-owned empty state driven entirely by resolved descriptors. */
export function TableEmptyState({
  title,
  message,
  actions,
  onAction,
  actionContext,
  readOnly,
}: TableEmptyStateProps) {
  const { t } = useTranslation();

  return (
    <Box
      role="status"
      data-testid="table-empty-state"
      sx={{
        textAlign: 'center',
        py: { xs: 4, sm: 5 },
        px: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.25,
      }}
    >
      {title && (
        <Typography variant="h6" component="p" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
      )}
      {message && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, lineHeight: 1.55 }}>
          {message}
        </Typography>
      )}
      {readOnly && (
        <Typography variant="caption" color="text.disabled" sx={{ maxWidth: 420 }}>
          {t('table.emptyState.readOnlyHint')}
        </Typography>
      )}
      {actions && actions.length > 0 && onAction && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            justifyContent: 'center',
            mt: 0.75,
          }}
        >
          {actions.map(action => (
            <ActionRenderer
              key={action.action.id}
              action={action}
              context={actionContext ?? { source: 'page' }}
              onAction={onAction}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
